'use client';

import Link from 'next/link';

import { useCookieConsent } from '@/components/layout/CookieConsent/CookieConsentProvider';
import { Button } from '@/components/primitives/Button';
import { COOKIE_SETTINGS_PATH } from '@/constants/cookies';
import { localizedHref, type Locale } from '@/lib/i18n';

import styles from './CookieBanner.module.css';

import type { Messages } from '@/content/messages';

type CookieBannerProps = {
  locale: Locale;
  messages: Messages['cookies']['banner'];
};

export function CookieBanner({ locale, messages }: CookieBannerProps) {
  const { ready, isBannerOpen, acceptAnalytics, rejectAnalytics } = useCookieConsent();
  const titleId = 'cookie-banner-title';
  const descriptionId = 'cookie-banner-description';

  if (!ready || !isBannerOpen) {
    return null;
  }

  return (
    <aside
      className={styles.banner}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      {/* Keep brand mark LTR so the wordmark never mirrors in RTL */}
      <div className={styles.logoWrap} dir="ltr">
        {/* eslint-disable-next-line @next/next/no-img-element -- decorative brand mark (ADR-004) */}
        <img
          className={styles.logo}
          src="/svg/birdlife-logo-white.svg"
          alt=""
          width={99}
          height={72}
          aria-hidden
        />
      </div>

      <h2 className={styles.title} id={titleId}>
        {messages.title}
      </h2>

      <p className={styles.description} id={descriptionId}>
        {messages.description}
      </p>

      <Link className={styles.link} href={localizedHref(locale, COOKIE_SETTINGS_PATH)}>
        {messages.noticeLink}
      </Link>

      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          colour="white"
          size="medium"
          className={`${styles.actionButton} ${styles.actionReject}`}
          onClick={rejectAnalytics}
        >
          {messages.reject}
        </Button>

        <Button
          type="button"
          variant="primary"
          colour="blue"
          size="medium"
          className={`${styles.actionButton} ${styles.actionAccept}`}
          onClick={acceptAnalytics}
        >
          {messages.accept}
        </Button>
      </div>
    </aside>
  );
}
